import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
   ChevronDown, Search,
  Users, Tag, Calendar, RefreshCw, Filter, X, FilterX, Download
} from 'lucide-react';
import { 
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  LabelList, Legend
} from 'recharts';
import './DashboardBultos.css';
import DashboardMenuHeader from '../components/DashboardMenuHeader';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import { clearCache } from '../utils/dashboardCache';
import FullScreenLoader from './FullScreenLoader';
import GlobalSearch from '../components/GlobalSearch';
import { calculateWorkingDays } from '../utils/timeUtils';
import * as XLSX from 'xlsx';

/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const filterLabels = {
  BI_PROVEEDOR: "Casas",
  BI_MARCA: "Marca",
  BI_LINEA: "Línea",
  CLI_SEG_DES: "Segmento",
  CLI_PRIN: "Cliente",
  CLI_VEN_DES: "Vendedor",
  CLI_CANAL: "Canal",
  CLI_REG_VERD: "Región Filtro",
  V_ANULADA: "Factura Anulada",
  CLI_INACTIVO: "Cliente Inactivo",
  BI_CLASIF2: "Clasificación",
  BI_DESC1: "Producto",
  CLI_TERRITORIO: "Territorio",
  CLI_CANAL_REAL: "Canal 2",
  BI_SEGMENTO: "Portafolio",
  CLI_NOMBRE: "Cliente Detalle",
  V_FACTURA: "Factura",
  CLIENTE_ID: "Cliente ID",
  ART_ID: "SKU ID",
  BI_ASP: "ASP"
};

// ✅ FUNCIONES DE FORMATO ACTUALIZADAS
const formatNumber = (num, isUSD = false) => { // isUSD parameter added
  if (!num && num !== 0) return isUSD ? '$0' : '0'; // Default for empty/zero
  const value = num || 0;
  if (isUSD) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }
  return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

const formatK = (num, isUSD = false) => {
  if (!num && num !== 0) return isUSD ? '$0' : '0'; // Default for empty/zero
  const value = num || 0;
  // If value is large enough for 'K' suffix
  if (Math.abs(value) >= 1000) {
    const formattedValue = (value / 1000).toFixed(0); // 0 decimals for 'K'
    return isUSD ? `$${formattedValue}K` : `${formattedValue}K`;
  }
  // Otherwise, format without decimal places
  return formatNumber(value, isUSD); // Reuse formatNumber for consistency
};

const getSemaforoColor = (p) => {
  const val = Number(p || 0);
  if (val < 80) return '#8b0000'; // Rojo Oscuro
  if (val < 90) return '#ff4d4d'; // Rojo Claro
  if (val < 100) return '#ffcc00'; // Amarillo
  return '#00ba52'; // Verde
};

const nombresMeses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const INITIAL_FILTERS = {
  BI_PROVEEDOR: [], BI_MARCA: [], BI_LINEA: [], CLI_SEG_DES: [],
  CLI_PRIN: [], CLI_VEN_DES: [], CLI_CANAL: [], CLI_REG_VERD: [], 
  V_ANULADA: [], CLI_INACTIVO: [], BI_CLASIF2: [], BI_DESC1: [],
  CLI_TERRITORIO: [], CLI_CANAL_REAL: [], BI_SEGMENTO: [], CLI_NOMBRE : [],
  V_FACTURA: [], CLIENTE_ID: [], ART_ID: [], BI_ASP: []
};

// ✅ Componente MultiSelectFilter (sin cambios)
const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalOptions = options.length;
  const hasSelection = selectedValues?.length > 0;
  const allOptionsSelected = totalOptions > 0 && selectedValues.length === totalOptions;
  
  const displayValue = allOptionsSelected 
    ? 'Todos'
    : hasSelection 
      ? (selectedValues.length === 1 
          ? options.find(opt => (opt.value || opt) === selectedValues[0])?.label || selectedValues[0]
          : `${selectedValues.length} Selec.`) 
      : 'Todos';
  
  const showSearch = options.length > 12;
  const filteredOptions = options.filter(opt => 
    String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 100);
  
  const areAllSelected = filteredOptions.every(opt => 
    selectedValues.includes(opt.value || opt)
  );

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = selectedValues.filter(val => 
        !filteredOptions.some(opt => (opt.value || opt) === val)
      );
      onChange(name, newSelected);
    } else {
      const newValuesOnly = filteredOptions.map(opt => opt.value || opt);
      onChange(name, newValuesOnly);
    }
  };

  const handleSingleToggle = useCallback((optValue) => {
    const newVals = selectedValues.includes(optValue)
      ? selectedValues.filter(v => v !== optValue)
      : [...selectedValues, optValue];
    onChange(name, newVals);
  }, [selectedValues, onChange, name]);

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button 
        className={`filter-pill-button ${hasSelection ? 'active' : ''}`} 
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input 
                type="text" 
                placeholder="Buscar..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                autoFocus 
              />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input 
                type="checkbox" 
                checked={areAllSelected} 
                onChange={handleSelectAll} 
              />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value || opt;
              const optLabel = opt.label || opt;
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input 
                    type="checkbox" 
                    checked={selectedValues.includes(optValue)} 
                    onChange={() => handleSingleToggle(optValue)}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button className="reset-pill-link" onClick={() => onChange(name, [])}>
              Limpiar ({selectedValues.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const DashboardAsesores = () => {
  const { salesQueryAsesores, getSalesFilters, loading: apiLoading } = useQuery(); 
  const { profile } = useAuth();

  const [localLoading, setLocalLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [metrics, setMetrics] = useState({ 
    v_cantidad: 0, q_cuota: 0, porcentaje: 0
  });
  const [chartData, setChartData] = useState([]);
  const [selectedMeses, setSelectedMeses] = useState([nombresMeses[new Date().getMonth()]]);
  const [metricType, setMetricType] = useState('BULTOS'); // ✅ NUEVO ESTADO
  const currentYear = new Date().getFullYear();
  const [selectedAnios, setSelectedAnios] = useState([currentYear]);
  
  // ✅ ESTADOS PARA FILTROS INDEPENDIENTES DEL GRÁFICO
  const [viewType, setViewType] = useState('mensual');
  const [chartYear, setChartYear] = useState(currentYear);
  const currentMonthIdx = new Date().getMonth();
  const [chartSelectedMeses, setChartSelectedMeses] = useState(nombresMeses.slice(0, currentMonthIdx + 1));
  const [showOnlyRealChart, setShowOnlyRealChart] = useState(false); // ✅ NUEVO ESTADO
  const [barComparisonMode, setBarComparisonMode] = useState('CUOTA');
  const [variations, setVariations] = useState({ anual: 0 });

  // ✅ ESTADOS PARA LA TABLA DE DESEMPEÑO MENSUAL
  const [breakdownType, setBreakdownType] = useState('ASESOR');
  const [monthlyTableMeses, setMonthlyTableMeses] = useState(() => {
    const d = new Date();
    return nombresMeses.slice(0, d.getMonth() + 1);
  });
  const [showOnlyReal, setShowOnlyReal] = useState(isMobile);
  const [monthlyAdvisorData, setMonthlyAdvisorData] = useState([]);

  // ✅ ESTADOS PARA LA TABLA JERÁRQUICA REGIONAL
  const [expandedRegions, setExpandedRegions] = useState({});
  const [expandedAdvisors, setExpandedAdvisors] = useState({});
  const [expandedClients, setExpandedClients] = useState({});
  const [tableData, setTableData] = useState([]);
  const [clientActivationFilter, setClientActivationFilter] = useState('ALL'); // 'ALL', 'ACTIVE', 'INACTIVE'

  // ✅ Filtrado Dinámico para la Tabla Regional (Activados / Sin Activación)
  const filteredTableData = useMemo(() => {
    if (clientActivationFilter === 'ALL') return tableData;

    return tableData.map(reg => {
      const filteredAsesores = reg.asesores.map(adv => {
        const filteredClients = adv.clientes.filter(client => {
          // Activados: Solo clientes con venta real > 0
          if (clientActivationFilter === 'ACTIVE') return (client.venta || 0) > 0;
          // Sin Activación: Solo clientes con cuota > 0 pero venta <= 0
          if (clientActivationFilter === 'INACTIVE') return (client.venta || 0) <= 0 && (client.cuota || 0) > 0;
          return true;
        });

        if (filteredClients.length === 0) return null;
        return {
          ...adv,
          clientes: filteredClients,
          venta: filteredClients.reduce((sum, c) => sum + (c.venta || 0), 0),
          cuota: filteredClients.reduce((sum, c) => sum + (c.cuota || 0), 0),
          venta_mes_ant: filteredClients.reduce((sum, c) => sum + (c.venta_mes_ant || 0), 0),
          venta_anio_ant: filteredClients.reduce((sum, c) => sum + (c.venta_anio_ant || 0), 0)
        };
      }).filter(Boolean);

      if (filteredAsesores.length === 0) return null;
      return { ...reg, asesores: filteredAsesores, venta: filteredAsesores.reduce((sum, a) => sum + a.venta, 0), cuota: filteredAsesores.reduce((sum, a) => sum + a.cuota, 0), venta_mes_ant: filteredAsesores.reduce((sum, a) => sum + a.venta_mes_ant, 0), venta_anio_ant: filteredAsesores.reduce((sum, a) => sum + a.venta_anio_ant, 0) };
    }).filter(Boolean);
  }, [tableData, clientActivationFilter]);

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [availableOptions, setAvailableOptions] = useState({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [salesConfig, setSalesConfig] = useState(null);
  const [comparisonMode, setComparisonMode] = useState('CUOTA');
  const [devMode, setDevMode] = useState('NONE'); // NONE, DEV, FIN, NETO
  const requestRef = useRef(0);
  const filtersInitialized = useRef(false);
  const [workDayMetrics, setWorkDayMetrics] = useState({ total: 0, elapsed: 0, percentage: 0 });
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    const metrics = calculateWorkingDays(selectedMeses, selectedAnios, nombresMeses);
    setWorkDayMetrics(metrics);
  }, [selectedMeses, selectedAnios]);


  // ✅ FUNCIONES PARA EXPANDIR/COLAPSAR LA TABLA JERÁRQUICA
  const toggleRegion = (region) => {
    setExpandedRegions(prev => ({ ...prev, [region]: !prev[region] }));
  };

  const toggleAdvisor = (region, asesor) => {
    const key = `${region}|||${asesor}`;
    setExpandedAdvisors(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleClient = (region, asesor, cliente) => {
    const key = `${region}|||${asesor}|||${cliente}`;
    setExpandedClients(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Carga inicial de configuración
  useEffect(() => {
    const loadDashboardData = async () => {
      setLocalLoading(true);
      try {
        const config = await getSalesFilters();
        if (!config) throw new Error("Configuración no disponible.");
        
        setSalesConfig(config);
        setAvailableOptions(config.availableOptions || {});
        setAvailableYears(config.availableYears || [currentYear]);
        
        // Prefiltrar segmentos
        const allSegments = config.availableOptions?.CLI_SEG_DES || [];
        const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
        const prefilteredSegments = allSegments.filter(segment => 
          !excludedSegments.some(ex => segment.toUpperCase().includes(ex.toUpperCase()))
        );
        
        const prefilteredFilters = {
          ...INITIAL_FILTERS,
          V_ANULADA: ['0'],
          CLI_INACTIVO: ['0'],
          CLI_SEG_DES: prefilteredSegments
        };
        
        setFilters(prefilteredFilters);
        filtersInitialized.current = true;
        
      } catch (err) {
        console.error('Error al cargar configuración:', err);
      } finally {
        setLocalLoading(false);
      }
    };
    loadDashboardData();
  }, []);

  const handleRefresh = async () => {
    clearCache();
    await fetchData();
  };

  const handleResetFilters = useCallback(() => {
    const config = salesConfig;
    if (!config?.availableOptions?.CLI_SEG_DES) return;
    
    const allSegments = config.availableOptions.CLI_SEG_DES;
    const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
    const prefilteredSegments = allSegments.filter(segment => 
      !excludedSegments.some(ex => segment.toUpperCase().includes(ex.toUpperCase()))
    );
    
    const resetFilters = {
      ...INITIAL_FILTERS,
      V_ANULADA: ['0'],
      CLI_INACTIVO: ['0'],
      CLI_SEG_DES: prefilteredSegments
    };
    
    setFilters(resetFilters);
  }, [salesConfig]);

  // ✅ FUNCIÓN DE EXPORTACIÓN PARA LA TABLA MENSUAL
  const exportTableToExcel = (data, filename, type) => {
    let rows = [];
    if (type === 'regional') {
      data.forEach(reg => {
        reg.asesores.forEach(adv => {
          adv.clientes.forEach(cli => {
            cli.marcasDetail.forEach(marDetail => {
              rows.push({
                Región: reg.region, Asesor: adv.asesor,
                Cod_Cliente: marDetail.id_cliente, Cliente: cli.cliente, Marca: marDetail.marca,
                Cuota: Math.round(marDetail.cuota || 0), Real: Math.round(marDetail.venta || 0), 
                Efec_Pct: Math.round(marDetail.cuota > 0 ? (marDetail.venta / marDetail.cuota) * 100 : 0)
              });
            });
          });
        });
      });
    } else if (type === 'monthly') {
      const entityLabel = breakdownType === 'ASESOR' ? 'Asesor' : (breakdownType === 'CLIENTE' ? 'Cliente' : (breakdownType === 'REGION' ? 'Región' : (breakdownType === 'PROVEEDOR' ? 'Casa' : 'Canal')));
      const sortedM = [...monthlyTableMeses].sort((a, b) => nombresMeses.indexOf(a) - nombresMeses.indexOf(b));
      
      data.forEach(adv => {
        const row = { [entityLabel]: adv.entidad };
        sortedM.forEach(mesName => {
          const m = adv.months[nombresMeses.indexOf(mesName)] || { venta: 0, cuota: 0, logro: 0 };
          row[`${mesName}_Cuota`] = Math.round(m.cuota);
          row[`${mesName}_Real`] = Math.round(m.venta);
          row[`${mesName}_Logro`] = Math.round(m.logro) + '%';
        });
        row.Total_Real = Math.round(adv.totalReal);
        row.Total_Cuota = Math.round(adv.totalCuota);
        row.Total_Logro = (adv.totalCuota > 0 ? Math.round((adv.totalReal / adv.totalCuota) * 100) : 0) + '%';
        rows.push(row);
      });
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, `${filename}_${new Date().getTime()}.xlsx`);
  };

  const processResponse = (response) => {
    const kpis = Array.isArray(response.kpis) ? response.kpis[0] || {} : (response.kpis || {});
    const v_actual = Number(kpis.v_actual || 0);
    const q_actual = Number(kpis.q_actual || 0);
    const porcentaje = q_actual > 0 ? Math.round((v_actual / q_actual) * 100 * 10) / 10 : 0;

    const metrics = { 
      v_cantidad: v_actual, 
      q_cuota: q_actual, 
      porcentaje,
      var_v_anual: kpis.var_v_anual || 0 
    };

    // Procesar datos del gráfico de desempeño
    const isPrevYear = barComparisonMode === 'PREV_YEAR';
    const processedChartData = (response.trend_adv || []).map(item => {
      const venta = Number(item.Venta || 0);
      const prevYearVenta = Number(item.Venta_PY || 0);
      const cuota = Number(item.Cuota || 0);
      const logro = Number(item.Logro || 0);
      const variacionReal = Number(item.Var_Venta_Anual_Pct || 0);

      return {
        ...item,
        ComparisonValue: isPrevYear ? prevYearVenta : cuota,
        Cuota: cuota,
        Venta: venta,
        RealAnterior: prevYearVenta,
        Logro: isPrevYear ? variacionReal : logro,
        VariacionReal: variacionReal,
        originalLogro: logro,
        originalCuota: cuota
      };
    });

    // ✅ PROCESAR MATRIZ MENSUAL PARA LA TABLA
    const advisorMap = {};
    (response.asesores_monthly || []).forEach(row => {
      if (!advisorMap[row.entidad]) {
        advisorMap[row.entidad] = {
          entidad: row.entidad,
          totalReal: 0,
          totalCuota: 0,
          months: Array(12).fill(null)
        };
      }
      const v = Number(row.venta || 0);
      const q = Number(row.cuota || 0);
      const mIdx = Number(row.mes_num) - 1;
      if (mIdx >= 0 && mIdx < 12) {
        advisorMap[row.entidad].months[mIdx] = {
          venta: v,
          cuota: q,
          logro: q > 0 ? (v / q) * 100 : 0
        };
        advisorMap[row.entidad].totalReal += v;
        advisorMap[row.entidad].totalCuota += q;
      }
    });
    const processedMonthlyData = Object.values(advisorMap).sort((a, b) => b.totalReal - a.totalReal);

    // ✅ PROCESAR TABLA REGIONAL JERÁRQUICA
  

    const chartData = processedChartData
      .filter(item => {
        if (viewType === 'mensual') {
          const anio = Number(item.anio_num);
          const mesNum = Number(item.mes_num);
          const selectedMonthNums = chartSelectedMeses.map(m => nombresMeses.indexOf(m) + 1).filter(n => n > 0);
          const matchesYear = anio === chartYear;
          const matchesMonth = selectedMonthNums.length === 0 || selectedMonthNums.includes(mesNum);
          return matchesYear && matchesMonth;
        }
        return true;
      })
      .filter(item => item.ComparisonValue > 0 || item.Venta > 0)
      .sort((a, b) => {
        if (a._anio !== b._anio) return a._anio - b._anio;
        return a._mes - b._mes;
      });

    return { metrics, chartData, monthlyAdvisorData: processedMonthlyData, variations: { anual: metrics.var_v_anual } };
  };

  // ✅ fetchData ACTUALIZADO
  const fetchData = useCallback(async () => {
    if (!filtersInitialized.current) return;
    
    const requestId = ++requestRef.current;
    setQuerying(true);
    try {
      const safeAnios = Array.isArray(selectedAnios) ? selectedAnios : [];
      const safeMeses = Array.isArray(selectedMeses) ? selectedMeses : [];

      const payload = {
        metric_type: metricType, 
        view_type: viewType,
        comparison_mode: comparisonMode,
        dev_mode: devMode, // ✅ ENVIAR AL BACKEND
        page_anios: safeAnios.filter(a => a),
        page_meses: safeMeses.filter(m => m),
        filters: filters,
        breakdown_type: breakdownType, // ✅ Usar el tipo de desglose de la tabla
        chart_year: chartYear,
        chart_meses: viewType === 'mensual' ? chartSelectedMeses : [], // ✅ Solo meses cuando la vista es mensual
        user_info: profile ? {
          username: profile.username,
          cargo: profile.cargo
        } : null
      }; // ✅ Usar el tipo de desglose de la tabla

      const rawResponse = await salesQueryAsesores(payload);
      if (requestId !== requestRef.current) return;

      const processed = processResponse(rawResponse);
      setMetrics(processed.metrics);
      setChartData(processed.chartData);
      setMonthlyAdvisorData(processed.monthlyAdvisorData);
      setTableData(processed.tableData);
      setVariations(processed.variations);

    } catch (err) {
      if (requestId === requestRef.current) {
        console.error("Error fetchData:", err);
      }
    } finally {
      if (requestId === requestRef.current) {
        setQuerying(false);
      }
    }
  }, [metricType, comparisonMode, devMode, selectedMeses, selectedAnios, filters, salesQueryAsesores, profile, viewType, chartYear, chartSelectedMeses, barComparisonMode, breakdownType]); 
  // Add clientActivationFilter to dependencies
  // Debounced auto-fetch ACTUALIZADO
  const debouncedFetch = useRef();
  useEffect(() => {
    if (!filtersInitialized.current) return;
    
    if (debouncedFetch.current) {
      clearTimeout(debouncedFetch.current);
    }
    
    debouncedFetch.current = setTimeout(() => {
      fetchData();
    }, 300);
    
    return () => {
      if (debouncedFetch.current) {
        clearTimeout(debouncedFetch.current);
      }
    };
}, [metricType, comparisonMode, devMode, selectedMeses, selectedAnios, filters, viewType, chartYear, chartSelectedMeses, barComparisonMode, breakdownType]);

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  // ✅ CustomChartTooltip ACTUALIZADO
  const CustomChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const isUSD = metricType === 'USD';
      const isPrevYear = barComparisonMode === 'PREV_YEAR';

      // ✅ Detectar qué items están presentes en el payload
      const realActualItem = payload.find(p => p.name === "Real Actual");
      const comparisonItem = payload.find(p => ["Real Año Ant.", "Forecast", "Cuota"].includes(p.name));
      const achievementItem = payload.find(p => ["Variación", "Logro"].includes(p.name));

      return (
        <div className="custom-tooltip" style={{ backgroundColor: 'white', padding: '10px', borderRadius: '5px', boxShadow: '0 2px 10px rgba(0,0,0,0.2)', border: '1px solid #ccc' }}>
          <p style={{ color: '#333', fontWeight: 'bold', marginBottom: '5px' }}>{label}</p>
          {comparisonItem && (
            <p style={{ color: '#1a2c4e', fontSize: '0.8rem', margin: 0 }}>
              {comparisonItem.name}: {formatNumber(comparisonItem.value, isUSD)}
            </p>
          )}
          <p style={{ color: '#0068b3', fontSize: '0.8rem', margin: 0 }}>
            Real Actual ({isUSD ? 'USD' : 'Bultos'}): {formatNumber(realActualItem?.value || 0, isUSD)}
          </p>
          {achievementItem && (
            <p style={{ color: isPrevYear ? (achievementItem.value >= 0 ? '#00ba52' : '#ff4d4d') : '#00ba52', fontSize: '0.8rem', margin: '5px 0 0', fontWeight: 'bold' }}>
              {achievementItem.name}: {achievementItem.value}%
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const filterTitle = ` - ${selectedMeses.join(', ')} - ${selectedAnios.join(', ')}`;

  // ✅ CÁLCULOS DINÁMICOS PARA EL GRID DE LA TABLA
  const sortedMonths = [...monthlyTableMeses].sort((a, b) => nombresMeses.indexOf(a) - nombresMeses.indexOf(b));
  const columnsPerMonth = showOnlyReal ? 1 : 3;
  const totalColumns = (sortedMonths.length + 1) * columnsPerMonth; // +1 por la columna TOTAL
  
  const firstColWidth = isMobile ? '110px' : '180px';
  const minColWidth = isMobile ? (showOnlyReal ? 70 : 50) : (showOnlyReal ? 80 : 60);

  const dynamicGridStyle = {
    display: 'grid', 
    gridTemplateColumns: `${firstColWidth} repeat(${totalColumns}, 1fr)`,
    minWidth: `${parseInt(firstColWidth) + totalColumns * minColWidth}px`,
    textAlign: 'right',
    gap: '0', 
    padding: '0', 
    alignItems: 'stretch', 
  };

  return (
    <motion.div 
      className="dashboard-container"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      transition={isMobile ? { duration: 0.3 } : { duration: 0.6, ease: "easeInOut" }}
    >
      <DashboardMenuHeader />

      {(apiLoading || localLoading) && !filtersInitialized.current ? (
        <FullScreenLoader message="Cargando datos del servidor..." /> 
      ) : null}
      
      {querying && <div className="querying-overlay active" />}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={isMobile ? { duration: 0.2 } : { duration: 0.5, ease: "easeOut" }}
      >
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">
              {metricType === 'USD' ? 'Ventas USD' : 'Bultos Vendidos'} {/* ✅ Título dinámico */}
            </h1>
            <div className="title-underline"></div>
          </div>
          <div className="date-selectors">
            <button 
              className="refresh-button" 
              onClick={handleRefresh}
              title="Actualizar Datos"
              disabled={querying}
            >
              <RefreshCw size={16} className={querying ? "spin-anim" : ""} />
            </button>
            <button 
              className="mobile-filter-btn" 
              onClick={() => setShowMobileFilters(true)}
            >
              <Filter size={16} />
              <span>Filtros</span>
            </button>
            <div className="switch-toggle-group">
              <button className={`switch-toggle-btn ${comparisonMode === 'CUOTA' ? 'active' : ''}`} onClick={() => setComparisonMode('CUOTA')}>Cuota</button>
              <button className={`switch-toggle-btn ${comparisonMode === 'FORECAST' ? 'active' : ''}`} onClick={() => setComparisonMode('FORECAST')}>Forecast</button>
            </div>
            {/* ✅ NUEVO BOTÓN DE DEVOLUCIONES */}
            <div className="switch-toggle-group">
              <div className="switch-toggle-group">
              <button className={`switch-toggle-btn ${devMode === 'NONE' ? 'active' : ''}`} onClick={() => setDevMode('NONE')}>Sin Dev.</button>
              <button className={`switch-toggle-btn ${devMode === 'DEV' ? 'active' : ''}`} onClick={() => setDevMode('DEV')}>Con Dev.</button>
              <button className={`switch-toggle-btn ${devMode === 'FIN' ? 'active' : ''}`} onClick={() => setDevMode('FIN')}>Con D.FIN</button>
              <button className={`switch-toggle-btn ${devMode === 'NETO' ? 'active' : ''}`} onClick={() => setDevMode('NETO')}>NETO</button>
            </div>
            </div>
            
            {/* ✅ NUEVO BOTÓN DE MÉTRICA */}
            <div className="switch-toggle-group">
              <button 
                className={`switch-toggle-btn ${metricType === 'BULTOS' ? 'active' : ''}`} 
                onClick={() => setMetricType('BULTOS')}
              >
                BULTOS
              </button>
              <button 
                className={`switch-toggle-btn ${metricType === 'USD' ? 'active' : ''}`} 
                onClick={() => setMetricType('USD')}
              >
                USD
              </button>
            </div>

            <div className="date-filters-group">
              <MultiSelectFilter 
                label="Mes" 
                name="meses" 
                icon={Calendar} 
                options={nombresMeses} 
                selectedValues={selectedMeses} 
                onChange={(name, vals) => setSelectedMeses(vals)} 
              />
              <MultiSelectFilter 
                label="Año" 
                name="anios" 
                icon={Calendar} 
                options={availableYears.map(year => ({value: year, label: year.toString()}))} 
                selectedValues={selectedAnios} 
                onChange={(name, vals) => setSelectedAnios(vals)} 
              />
            </div>
            <div style={isMobile ? { width: '100%', marginTop: '8px' } : {}}>
              <GlobalSearch 
                availableOptions={availableOptions}
                filterLabels={filterLabels}
                filters={filters}
                setFilters={setFilters}
              />
            </div>
          </div>
        </header>

        {/* ✅ Sidebar de filtros (completo) */}
        <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
          <div className="filters-sidebar-header">
            <h3>Filtros Disponibles</h3>
            <div className="filters-header-actions">
              <button className="reset-filters-btn" onClick={handleResetFilters} title="Restablecer filtros">
                <FilterX size={16} />
              </button>
              <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}>
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="filters-grid" style={{ gap: '8px' }}>
            {Object.keys(filters).map((key) => (
              <MultiSelectFilter 
                key={key} 
                label={filterLabels[key] || key} 
                name={key} 
                icon={key.includes('CLI') ? Users : Tag} 
                options={availableOptions[key] || []}
                selectedValues={filters[key] || []}
                onChange={(name, vals) => {
                  setFilters(prev => ({ ...prev, [name]: vals }));
                }} 
              />
            ))}
            <button className="filter-pill-button reset-filters" onClick={handleResetFilters} title="Restablecer filtros">
              <FilterX size={12} className="pill-icon" />
              <span className="pill-label">Limpiar Todos</span>
            </button>
          </div>
        </div>
        {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

        {/* ✅ GRÁFICO ACTUALIZADO */}
        <div className="full-width-chart-container" style={{ opacity: querying ? 0.7 : 1, transition: 'opacity 0.3s' }}>
          <div className="asesores-chart-header">
            <div className="chart-header-title">
              <h3 className="chart-title">
                Desempeño Comparativo ({barComparisonMode === 'CUOTA' ? (comparisonMode === 'FORECAST' ? 'FC' : 'Cuota') : 'vs Año Ant.'}) - {metricType} - {viewType === 'mensual' ? `Año ${chartYear}` : 'Histórico Anual'}
              </h3>
            </div>
            <div className="chart-header-filters" style={{ gap: '8px' }}>
              <div className="switch-toggle-group">
                <button 
                  className={`switch-toggle-btn ${barComparisonMode === 'CUOTA' ? 'active' : ''}`}
                  onClick={() => setBarComparisonMode('CUOTA')}
                >
                  Vs Cuota
                </button>
                <button 
                  className={`switch-toggle-btn ${barComparisonMode === 'PREV_YEAR' ? 'active' : ''}`}
                  onClick={() => setBarComparisonMode('PREV_YEAR')}
                >
                  Vs Año Ant.
                </button>
              </div>
              <div className="switch-toggle-group">
                <button 
                  className={`switch-toggle-btn ${showOnlyRealChart ? 'active' : ''}`}
                  onClick={() => setShowOnlyRealChart(!showOnlyRealChart)}
                >
                  {showOnlyRealChart ? 'Ver Comparativa' : 'Solo Real'}
                </button>
              </div>
              <div className="switch-toggle-group">
                <button className={`switch-toggle-btn ${viewType === 'mensual' ? 'active' : ''}`} onClick={() => setViewType('mensual')}>Mensual</button>
                <button className={`switch-toggle-btn ${viewType === 'anual' ? 'active' : ''}`} onClick={() => setViewType('anual')}>Anual</button>
              </div>
              {viewType === 'mensual' && (
                <div style={{ display: 'flex', gap: '4px' }}>
                  <MultiSelectFilter 
                    label="Mes" 
                    name="chartMeses" 
                    icon={Calendar} 
                    options={nombresMeses} 
                    selectedValues={chartSelectedMeses} 
                    onChange={(name, vals) => setChartSelectedMeses(vals)} 
                  />
                  <MultiSelectFilter 
                    label="Año" 
                    name="chartYear" 
                    icon={Calendar} 
                    options={isMobile ? [new Date().getFullYear()] : availableYears.map(year => ({ value: year, label: year.toString() }))} 
                    selectedValues={[chartYear]} 
                    onChange={(name, vals) => { if (vals.length > 0) setChartYear(vals[vals.length - 1]); }} 
                  />
                </div>
              )}
            </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <ComposedChart data={chartData} margin={{ top: 20, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff20" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#fff' }} />
                <YAxis 
                  yAxisId="left" 
                  axisLine={false} 
                  tickLine={false} 
                  tickFormatter={(value) => formatK(value, metricType === 'USD')} 
                  tick={{ fontSize: 10, fill: '#fff' }} 
                />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={false} width={0} domain={barComparisonMode === 'PREV_YEAR' ? ['auto', 'auto'] : [0, (max) => Math.max(max, 100) * 1.1]} />
                <Tooltip content={<CustomChartTooltip />} />
                <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ paddingBottom: '15px', fontSize: '11px', fill: '#fff' }} />
                {!showOnlyRealChart && (
                  <Bar 
                    yAxisId="left" 
                    name={barComparisonMode === 'PREV_YEAR' ? 'Real Año Ant.' : (comparisonMode === 'FORECAST' ? 'Forecast' : 'Cuota')} 
                    dataKey={barComparisonMode === 'PREV_YEAR' ? 'ComparisonValue' : 'Cuota'} 
                    fill="#3c4b68" 
                    radius={[4, 4, 0, 0]} 
                    barSize={20}
                  >
                    <LabelList 
                      dataKey={barComparisonMode === 'PREV_YEAR' ? 'ComparisonValue' : 'Cuota'} 
                      position="top" 
                      formatter={(value) => formatK(value, metricType === 'USD')}
                      style={{ fontSize: '11px', fontWeight: 'bold', fill: '#fff' }} 
                    />
                  </Bar>
                )}
                <Bar yAxisId="left" name="Real Actual" dataKey="Venta" fill="#0068b3" radius={[4, 4, 0, 0]} barSize={20}>
                  <LabelList 
                      dataKey="Venta" 
                      position="top" 
                      formatter={(value) => formatK(value, metricType === 'USD')}
                      style={{ fontSize: '11px', fontWeight: 'bold', fill: '#fff' }} 
                    />
                </Bar>
                {!showOnlyRealChart && (
                  <Line yAxisId="right" name={barComparisonMode === 'PREV_YEAR' ? 'Variación' : 'Logro'} type="monotone" dataKey="Logro" stroke="#00ba52" strokeWidth={3} dot={{ r: 4, fill: '#00ba52' }}>
                    <LabelList 
                      dataKey="Logro" 
                      position="top" 
                      content={(props) => {
                        const { x, y, value } = props;
                        const color = barComparisonMode === 'PREV_YEAR' ? (value >= 0 ? '#00ba52' : '#ff4d4d') : '#00ba52';
                        const sign = barComparisonMode === 'PREV_YEAR' && value > 0 ? '+' : '';
                        return (
                          <text x={x} y={y} dy={-10} fill={color} fontSize={11} fontWeight="bold" textAnchor="middle">
                            {sign}{value}%
                          </text>
                        );
                      }} 
                    />
                  </Line>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* --- TABLA DE DESEMPEÑO MENSUAL POR ASESOR --- */}
        <div className="full-width-chart-container" style={{ marginTop: '20px', opacity: querying ? 0.7 : 1, transition: 'opacity 0.3s', height: '550px', display: 'flex', flexDirection: 'column' }}>
          <div className="asesores-chart-header">
            <div className="table-header-container" style={{ width: '100%', marginBottom: 0 }}>
              <h3 className="chart-title">Desempeño Mensual por {breakdownType}</h3>
              <button className="export-table-btn" onClick={() => exportTableToExcel(monthlyAdvisorData, 'Desempeño_Mensual', 'monthly')}>
                <Download size={14} /> Exportar
              </button>
            </div>
            <div className="chart-header-filters" style={{ flexWrap: 'wrap', gap: '8px' }}>
              <div className="switch-toggle-group">
                <button className={`switch-toggle-btn ${breakdownType === 'ASESOR' ? 'active' : ''}`} onClick={() => setBreakdownType('ASESOR')}>Asesor</button>
                <button className={`switch-toggle-btn ${breakdownType === 'REGION' ? 'active' : ''}`} onClick={() => setBreakdownType('REGION')}>Región</button>
                <button className={`switch-toggle-btn ${breakdownType === 'CLIENTE' ? 'active' : ''}`} onClick={() => setBreakdownType('CLIENTE')}>Cliente</button>
                <button className={`switch-toggle-btn ${breakdownType === 'CANAL' ? 'active' : ''}`} onClick={() => setBreakdownType('CANAL')}>Canal</button>
                <button className={`switch-toggle-btn ${breakdownType === 'PROVEEDOR' ? 'active' : ''}`} onClick={() => setBreakdownType('PROVEEDOR')}>Casa</button>
              </div>
              <div className="switch-toggle-group">
              <MultiSelectFilter 
                label="Meses Tabla" 
                name="monthlyTableMeses" 
                icon={Calendar} 
                options={nombresMeses} 
                selectedValues={monthlyTableMeses} 
                onChange={(_name, vals) => setMonthlyTableMeses(vals)} 
              />
              <button 
                className={`switch-toggle-btn ${showOnlyReal ? 'active' : ''}`} 
                onClick={() => setShowOnlyReal(!showOnlyReal)}
                style={{ borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)', background: showOnlyReal ? 'var(--bg-accent-hover)' : 'rgba(0,0,0,0.2)' }}
              >
                Solo Real
              </button>
              </div>
            </div>
          </div>

          <div className="minimal-table-wrapper" style={{ maxHeight: '450px', overflow: 'auto' }}>
            {/* Header wrapper with proper column alignment */}
            <div style={{ position: 'sticky', top: 0, zIndex: 20, backgroundColor: '#10172a', minWidth: dynamicGridStyle.minWidth, boxShadow: '0 2px 5px rgba(0,0,0,0.3)' }}>
              <div className="table-header monthly-header-grid" style={{...dynamicGridStyle, textAlign: 'center', padding: '0'}}>
                {/* Asesor Cell - Spanning 2 rows */}
                <div 
                  className="col-name" 
                  style={{
                    gridRow: '1 / 3', 
                    textAlign: 'left', 
                    paddingLeft: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'sticky',
                    left: 0,
                    zIndex: 25,
                    backgroundColor: '#10172a',
                    borderRight: '1px solid rgba(255,255,255,0.1)',
                    height: 'auto', /* Se estira con el grid */
                    boxShadow: '2px 0 5px rgba(0,0,0,0.5)' 
                  }}
                >
                  {breakdownType === 'ASESOR' ? 'Asesor' : (breakdownType === 'CLIENTE' ? 'Cliente' : (breakdownType === 'REGION' ? 'Región' : (breakdownType === 'PROVEEDOR' ? 'CASA' : 'Canal')))}
                </div>

                {/* Month and Total Headers - Row 1 */}
                {sortedMonths.map(mes => (
                  <div className="col-month-group" key={mes} style={{ gridColumn: `span ${columnsPerMonth}`, textAlign: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>{mes}</div>
                ))}
                <div className="col-month-group" style={{ gridColumn: `span ${columnsPerMonth}`, textAlign: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>TOTAL</div>

                {/* Sub-Headers - Row 2 */}
                {[...Array(sortedMonths.length + 1)].map((_, i) => (
                  <React.Fragment key={i}>
                    {!showOnlyReal && <div className="col-data" style={{ fontSize: '0.65rem', textAlign: 'center', padding: '4px 0' }}>{comparisonMode === 'FORECAST' ? 'FC' : 'Cuota'}</div>}
                    <div className="col-data" style={{ fontSize: '0.65rem', textAlign: 'center', padding: '4px 0' }}>Real</div>
                    {!showOnlyReal && <div className="col-data" style={{ fontSize: '0.65rem', textAlign: 'center', padding: '4px 0' }}>% Logro</div>}
                  </React.Fragment>
                ))}
              </div>
            </div>
            
            {/* Table Body */}
            <div className="table-body monthly-table-body" style={{ overflow: 'visible', minWidth: dynamicGridStyle.minWidth }}>
              {monthlyAdvisorData.map((adv, idx) => {
                const totalLogro = adv.totalCuota > 0 ? (adv.totalReal / adv.totalCuota) * 100 : 0;
                const prefix = metricType === 'USD' ? '$' : '';
                return (
                  <div className="table-row monthly-table-row" style={{...dynamicGridStyle, padding: '0'}} key={idx}>
                    <div 
                      className="col-name truncate" 
                      title={adv.entidad} 
                      style={{ 
                        paddingLeft: '10px', 
                        fontWeight: 400, 
                        fontSize: isMobile ? '0.55rem' : '0.65rem',
                        textAlign: 'left',
                        position: 'sticky', left: 0, zIndex: 10, backgroundColor: '#10172a', borderRight: '1px solid rgba(255,255,255,0.05)',
                        display: 'flex', alignItems: 'center', height: 'auto', boxShadow: '2px 0 5px rgba(0,0,0,0.2)'
                      }}
                    >
                      {adv.entidad}
                    </div>
                    {sortedMonths.map((mesName, mIdx) => {
                      const realMonthIndex = nombresMeses.indexOf(mesName);
                      const monthData = adv.months[realMonthIndex];
                      const m = monthData || { venta: 0, cuota: 0, logro: 0 };
                      return (
                        <React.Fragment key={mIdx}>
                          {!showOnlyReal && <div className="col-data" style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '8px 4px' }}>{prefix}{formatNumber(m.cuota)}</div>}
                          <div className="col-data" style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '8px 4px' }}>{prefix}{formatNumber(m.venta)}</div>
                          {!showOnlyReal && <div className="col-data monthly-logro-cell" style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '8px 4px', gap: '4px' }}>
                            <span className="logro-text">{m.logro.toFixed(0)}%</span>
                            <div className="status-dot semaforo-dot" style={{ backgroundColor: getSemaforoColor(m.logro), margin: 0 }}></div>
                          </div>}
                        </React.Fragment>
                      );
                    })}
                    {/* TOTAL column */}
                    {!showOnlyReal && <div className="col-data bold" style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '8px 4px' }}>{prefix}{formatNumber(adv.totalCuota)}</div>}
                    <div className="col-data bold" style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '8px 4px' }}>{prefix}{formatNumber(adv.totalReal)}</div>
                    {!showOnlyReal && <div className="col-data bold monthly-logro-cell" style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '8px 4px', gap: '4px' }}>
                      <span>{totalLogro.toFixed(1)}%</span>
                      <div className="status-dot semaforo-dot" style={{ backgroundColor: getSemaforoColor(totalLogro), margin: 0 }}></div>
                    </div>}
                  </div>
                );
              })}
            </div>

            {/* Table Footer */}
            <div 
              className="table-footer monthly-table-footer" 
              style={{
                ...dynamicGridStyle, 
                padding: '0',
                position: 'sticky', bottom: 0, zIndex: 20, backgroundColor: '#10172a',
                boxShadow: '0 -2px 5px rgba(0,0,0,0.3)'
              }}
            >
              <div 
                className="col-name" 
                style={{ 
                  fontWeight: 500, 
                  fontSize: '0.6rem',
                  textAlign: 'left',
                  position: 'sticky', left: 0, zIndex: 25, backgroundColor: '#10172a', borderRight: '1px solid rgba(255,255,255,0.1)',
                  paddingLeft: '10px',
                  display: 'flex', alignItems: 'center', height: 'auto', boxShadow: '2px 0 5px rgba(0,0,0,0.5)'
                }}
              >
                TOTALES
              </div>
              {(() => {
                const monthlyTotals = Array(12).fill(null).map(() => ({ cuota: 0, venta: 0 }));
                monthlyAdvisorData.forEach(adv => {
                  adv.months.forEach((month, i) => {
                    if (month) {
                      monthlyTotals[i].cuota += month.cuota;
                      monthlyTotals[i].venta += month.venta;
                    }
                  });
                });

                const grandTotalCuota = monthlyTotals.reduce((acc, m) => acc + m.cuota, 0);
                const grandTotalVenta = monthlyTotals.reduce((acc, m) => acc + m.venta, 0);
                const grandTotalLogro = grandTotalCuota > 0 ? (grandTotalVenta / grandTotalCuota) * 100 : 0;
                const prefix = metricType === 'USD' ? '$' : '';

                return (
                  <>
                    {sortedMonths.map((mesName, i) => {
                      const total = monthlyTotals[nombresMeses.indexOf(mesName)];
                      const logro = total.cuota > 0 ? (total.venta / total.cuota) * 100 : 0;
                      return (
                        <React.Fragment key={i}>
                          {!showOnlyReal && <div className="col-data" style={{ fontSize: '0.55rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '10px 4px' }}>{prefix}{formatNumber(total.cuota, metricType === 'USD')}</div>}
                          <div className="col-data" style={{ fontSize: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '10px 4px' }}>{prefix}{formatNumber(total.venta, metricType === 'USD')}</div>
                          {!showOnlyReal && <div className="col-data bold monthly-logro-cell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '10px 4px', gap: '4px' }}>
                            <div>{logro.toFixed(0)}%</div>
                            <span className="status-dot semaforo-dot" style={{ backgroundColor: getSemaforoColor(logro), margin: 0 }}></span>
                          </div>}
                        </React.Fragment>
                      );
                    })}
                    {!showOnlyReal && <div className="col-data bold" style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '10px 4px' }}>{prefix}{formatNumber(grandTotalCuota, metricType === 'USD')}</div>}
                    <div className="col-data bold" style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '10px 4px' }}>{prefix}{formatNumber(grandTotalVenta, metricType === 'USD')}</div>
                    {!showOnlyReal && <div className="col-data bold monthly-logro-cell" style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '10px 4px', gap: '4px' }}>
                      <span>{grandTotalLogro.toFixed(0)}%</span>
                      <div className="status-dot semaforo-dot" style={{ backgroundColor: getSemaforoColor(grandTotalLogro), margin: 0 }}></div>
                    </div>}
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* ✅ FOOTER ACTUALIZADO */}
        <footer className="status-footer">
          {querying ? "SINCRONIZANDO..." : 
            `VISTA ACTUAL: ${metricType} - ${selectedMeses.length > 0 ? selectedMeses.join(', ') : 'TODOS'} ${selectedAnios.length > 0 ? selectedAnios.join(', ') : ''}`
          }
        </footer>
      </motion.div>
    </motion.div>
  );
};

export default DashboardAsesores;